import React from "react";
import { Button, Modal } from "mesh-component-library";
import { useLocale } from "../../../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../../../app-consts/translations";
import { CustomAddress, GoogleDestinationWithPlaceId, ServiceDetailsBreakdown } from "../../../../types";
import { GoogleLocationDisplay } from "./GoogleLocationDisplay";
import { CustomAddressDisplay } from "./CustomAddressDisplay";
import { CustomAddressSuccessPayload } from "../../actions";
import { resolveLatLngValue } from "../../../../utils/resolveLatLngValue";

type ConfirmLocationParams = (GoogleDestinationWithPlaceId | CustomAddress) & {
  hasBeenConfirmed: boolean;
  isCustomLocation?: boolean;
};

type CustomAddressProps = {
  currentLocation: CustomAddress;
  customLocation: CustomAddressSuccessPayload;
  isCustomAddress: true;
  isOpen: boolean;
  location: CustomAddress;
  onConfirmLocation: (data: ConfirmLocationParams) => void;
  onClose: () => void;
  onCustomAddressClose: () => void;
  service: google.maps.places.PlacesService;
  towServiceDetails: ServiceDetailsBreakdown;
  updateDestinationWithPredictionAddress: ({ service, origin, placeId }) => void;
};

type GoogleLocationProps = {
  currentLocation: CustomAddress;
  customLocation: CustomAddressSuccessPayload;
  isCustomAddress: false;
  isOpen: boolean;
  location: GoogleDestinationWithPlaceId;
  onClose: () => void;
  onCustomAddressClose: () => void;
  onConfirmLocation: (data: ConfirmLocationParams) => void;
  service: google.maps.places.PlacesService;
  towServiceDetails: ServiceDetailsBreakdown;
  updateDestinationWithPredictionAddress: ({ service, origin, placeId }) => void;
};

export type DetailedLocationReviewV2Props = CustomAddressProps | GoogleLocationProps;

export const DetailedLocationReviewV2 = ({
  currentLocation,
  customLocation,
  isCustomAddress,
  isOpen,
  location,
  onClose,
  onCustomAddressClose,
  onConfirmLocation,
  service,
  towServiceDetails,
  updateDestinationWithPredictionAddress,
}: DetailedLocationReviewV2Props) => {
  const getTranslatedText = useLocale();

  const handleConfirmLocation = () => {
    if (isCustomAddress) {
      const origin = {
        lat: resolveLatLngValue(currentLocation.geometry.location.lat),
        lng: resolveLatLngValue(currentLocation.geometry.location.lng),
      };
      updateDestinationWithPredictionAddress({ service, placeId: location.place_id, origin });
      onConfirmLocation({ ...customLocation, hasBeenConfirmed: true, isCustomLocation: true });
    } else {
      onConfirmLocation({ ...location, hasBeenConfirmed: true });
    }
  };

  const handleClose = () => {
    if (isCustomAddress) {
      onCustomAddressClose();
    } else {
      onClose();
    }
  };

  return (
    <>
      <Modal
        isActive={isOpen}
        hideTrigger={true}
        isActionSheet={true}
        isFlushMobile={true}
        size="xl"
        onClose={handleClose}
        data-testid="detailedLocationReviewV2-modal"
      >
        {isCustomAddress ? (
          <CustomAddressDisplay
            location={customLocation}
            towServiceDetails={towServiceDetails}
          />
        ) : (
          <GoogleLocationDisplay
            location={location as GoogleDestinationWithPlaceId}
            towServiceDetails={towServiceDetails}
          />
        )}
        <Modal.Footer style={{ marginTop: "auto", paddingRight: "1rem" }}>
          <Button hasUpgrade isBlock onClick={handleConfirmLocation}>
            {getTranslatedText(TRANSLATION_CONSTANTS.CONFIRM_DROP_OFF_LOCATION)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
